function normalizeWhitespace(text: string): string {
  return text.replace(/\s+/g, " ").trim();
}

/** Find excerpt in source text; exact match first, then case-insensitive whitespace-normalized fuzzy match. */
export function findExcerptRange(
  source: string,
  excerpt: string,
): { start: number; end: number } | null {
  const needle = excerpt.trim();
  if (!needle) {
    return null;
  }

  const exact = source.indexOf(needle);
  if (exact >= 0) {
    return { start: exact, end: exact + needle.length };
  }

  const normNeedle = normalizeWhitespace(needle).toLowerCase();
  if (!normNeedle) {
    return null;
  }

  let start = -1;
  let end = -1;
  let inMatch = false;
  let normMatched = 0;

  for (let i = 0; i < source.length; i += 1) {
    const ch = source[i] ?? "";
    const isSpace = /\s/.test(ch);

    if (!inMatch) {
      if (isSpace) {
        continue;
      }
      inMatch = true;
      start = i;
      normMatched = 0;
    }

    if (!isSpace) {
      const normCh = ch.toLowerCase();
      const expected = normNeedle[normMatched];
      if (!expected || normCh !== expected) {
        inMatch = false;
        i = start;
        continue;
      }
      normMatched += 1;
      end = i + 1;
      if (normMatched === normNeedle.length) {
        return { start, end };
      }
    }
  }

  return null;
}

function getPlainTextLength(node: Node): number {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent?.length ?? 0;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return 0;
  }
  let length = 0;
  for (const child of Array.from(node.childNodes)) {
    length += getPlainTextLength(child);
  }
  return length;
}

function wrapTextRange(container: HTMLElement, start: number, end: number): HTMLElement | null {
  if (start >= end) {
    return null;
  }

  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  let offset = 0;
  let startNode: Text | null = null;
  let startOffset = 0;
  let endNode: Text | null = null;
  let endOffset = 0;

  while (walker.nextNode()) {
    const textNode = walker.currentNode as Text;
    const length = textNode.length;
    if (!startNode && offset + length > start) {
      startNode = textNode;
      startOffset = start - offset;
    }
    if (!endNode && offset + length >= end) {
      endNode = textNode;
      endOffset = end - offset;
      break;
    }
    offset += length;
  }

  if (!startNode || !endNode) {
    return null;
  }

  const range = document.createRange();
  range.setStart(startNode, startOffset);
  range.setEnd(endNode, endOffset);

  const highlight = document.createElement("span");
  highlight.className = "source-highlight";
  try {
    range.surroundContents(highlight);
  } catch {
    const extracted = range.extractContents();
    highlight.appendChild(extracted);
    range.insertNode(highlight);
  }

  return highlight;
}

export function clearSourceHighlights(root: ParentNode): void {
  root.querySelectorAll(".source-highlight").forEach((el) => {
    const parent = el.parentNode;
    if (!parent) {
      return;
    }
    while (el.firstChild) {
      parent.insertBefore(el.firstChild, el);
    }
    parent.removeChild(el);
    if (parent instanceof HTMLElement || parent instanceof DocumentFragment) {
      parent.normalize();
    }
  });
}

export function highlightExcerptInElement(
  container: HTMLElement,
  sourceText: string,
  excerpt: string,
): HTMLElement | null {
  clearSourceHighlights(container);

  const range = findExcerptRange(sourceText, excerpt);
  if (!range) {
    return null;
  }

  const plainLength = getPlainTextLength(container);
  if (plainLength < range.end) {
    return null;
  }

  return wrapTextRange(container, range.start, range.end);
}

export function scrollHighlightIntoView(highlight: HTMLElement | null): void {
  highlight?.scrollIntoView({ behavior: "smooth", block: "center" });
}
